import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { RoleDto } from 'api/roles/models/RoleDto';
import Loading from 'common/services/Loading';
import RolesService from 'api/roles/RolesService';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import styles from './RolesDetails.module.scss';
import InputError from 'common/components/inputError/InputError';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import { useForm } from 'react-hook-form';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE } from 'Config';
import GreyArrow from 'assets/svg/Arrow-grey.svg';
import React, { useEffect, useState } from 'react';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { useHistory, useParams } from 'react-router-dom';
import { LanguageDto } from 'api/languages/models/LanguageDto';
import LanguagesService from 'api/languages/LanguagesService';
import LanguagesTab from 'common/components/languagesTabs/LanguagesTab';
import ContentManagement from 'common/components/contentManagement/ContentManagement';
import InputResizable from 'common/components/input/InputResizable';

const RolesDetails = ({ isAdmin }: { isAdmin?: boolean }) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const { childrenId: roleId, tabType, id: companyId } = useParams<{ childrenId: string, tabType: string, id: string }>();
    const [role, setRole] = useState<RoleDto>();
    const [isDetails, setIsDetails] = useState<boolean>(tabType === 'details');
    const [isEdit,setIsEdit] = useState<boolean>(tabType === 'edit');
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<RoleDto | null>(null);
    const history = useHistory();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasRolesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ROLES_WRITE', 'SETTINGUP_COMPANIES_WRITE'], 'OR');

    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [selectedLanguageIndex, setSelectedLanguageIndex] = useState(0);
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageDto | null>(null);

    const form = useForm<RoleDto>({ defaultValues: { ...role } });
    const { register, handleSubmit, errors } = form;

    const goToList = () => {
        if (companyId) {
            history.push(`/companies/details/${companyId}/roles`);
            return;
        }
        if (isAdmin) {
            history.push(`/roles`);
            return;
        }
        history.push(`/security/roles`);
    };

    const goToEdit = () => {
        setIsDetails(false)

        setIsEdit(true)
          if (companyId) {
            history.push(`/companies/details/${companyId}/roles/edit/${roleId}`);
        }

       else if (isAdmin) {
            history.push(`/roles/edit/${roleId}`);
        }
    }

    const onCancel = () => {
        if (isDetails) {
            goToList();
        } else if (!roleId) {
            goToList();
        }
        else {
            setIsDetails(true);
        }
    };

    const getData = async () => {

        Loading.show();
        try {
            const [_role, _languages] = await Promise.all([
                roleId ? RolesService.getById(roleId, companyId) : {
                    id: '',
                    name: '',
                    description: [],
                    readOnly: false,
                    system: false,
                    createdByAdmin:false,
                    checked:false
                } as RoleDto,
                LanguagesService.getAll()
            ]);

            setLanguages(_languages);
            setSelectedLanguageIndex(0)
            setSelectedLanguage(_languages[0])
            _role.internalName = _role.name
            if (_role && (_role.readOnly || _role.system)) {
                _role.name = t(('common.roles.' + _role.name) as any);
            }

            const existingLanguageIds = _role?.description.map(desc => desc.languageId);

            _languages.forEach(lang => {
                if (!existingLanguageIds.includes(lang.id)) {
                    _role?.description.push({
                        languageId: lang.id,
                        iso: lang.iso,
                        description: ''
                    });
                }
            });
            setRole(_role);
            Loading.hide();
        } catch (error) {
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't get catalog roles types`,
                error
            );
            Loading.hide();
        }
    }

    useEffect(() => {
        getData();
    }, [isDetails]);


    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }
        try {
            await RolesService.remove({ ...itemToRemove, companyId });
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
            onCancelRemove();
            goToList();
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete role`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const showRemoveItemDialog = async (item: RoleDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (formData: RoleDto) => {
        try {
            if (!hasRolesWritePolicy) { return; }
            const roleCompanyId = companyId ?? userProfile?.companyId;
            Loading.show();
            if(role && role.id != '') {
                await RolesService.update({ ...role,companyId:roleCompanyId });
            } else {
                await RolesService.create({ ...formData, createdByAdmin: isAdmin ?? false, companyId:roleCompanyId,description:role!.description });
            }

            Loading.hide();
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });
             goToList();
        } catch (error) {
            addToast(t('common.messages.record_save_error'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't create or update role`,
                error
            );
            Loading.hide();
        }
    };

    const onLanguageChange = (lang: LanguageDto, i: number) => {
        setLanguages([
            ...languages.map(l => {
                if (l.id === lang.id) {
                    return {
                        ...l,
                        languageTabVisited: true
                    }
                }
                return l;
            }),
        ]);
        setSelectedLanguage(lang);
        setSelectedLanguageIndex(i);
    }

    const copyLanguage = (langToCopyId: string) => {
        const sourceItem = role?.description.find(x => x.languageId == langToCopyId)?.description
        if (sourceItem) {
            setRole(prev => ({
                ...prev!,
                description: prev!.description.map(desc =>
                    desc.languageId === selectedLanguage!.id
                        ? { ...desc, description: sourceItem }
                        : desc
                )
            }));
        }
    }

    const updateDescriptionMessage = (newMessage: string) => {
        if (!role) return;
        setRole(prev => ({
            ...prev!,
            description: prev!.description.map(desc =>
                desc.languageId === selectedLanguage!.id
                    ? { ...desc, description: newMessage }
                    : desc
            )
        }));
    };
    return (
        <div className={styles.container}>
            <div className={styles.pageNameContainer}>
                <div className={styles.pageNameContent} onClick={() => goToList()} >
                    <img className={styles.pageNameContentImage} src={GreyArrow} />
                    <span className={styles.pageNameContentText}>
                        {t('roles.title')}
                    </span>
                </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} id="role-form">
                <FormItem>
                    <Label className={styles.label}>
                        {t('roles.name')}*
                    </Label>
                    <Input
                        name='name'
                        placeholder={t('roles.name')}
                        defaultValue={role?.name}
                        ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                        disabled={isDetails || isEdit}
                    />
                    <InputError error={errors.name} />
                </FormItem>


                <div>
                     <Label className={styles.label}>
                        {t('roles.description')}
                    </Label>
                    <LanguagesTab
                        selectedLanguageIndex={selectedLanguageIndex}
                        languages={languages}
                        onChange={onLanguageChange}
                    >
                        <div className={styles.containerWrapper}>
                            {languages.map((lang, index) => {
                                const keyName = `list.${index}`;

                                return <div key={lang.id + '_' + index}
                                    className={`${index != selectedLanguageIndex && styles.hideTab}`}>
                                    {!isDetails && <div className={styles.contentManagement}>
                                        <ContentManagement
                                            options={languages.filter(x => x.id != lang.id).map(x => ({ label: x.name, value: x.id }))}
                                            onCopy={lang => copyLanguage(lang)}
                                            isInnerContainer={true}
                                        />
                                    </div>}

                                    <div key={`form_${index}`}>
                                        <FormItem>
                                            <Label>{t('common.description')}</Label>


                                            <InputResizable
                                                onChange={e => {
                                                    updateDescriptionMessage(e.target.value)
                                                }}
                                                value={role?.description?.find(desc => desc.languageId === (selectedLanguage?.id ?? languages[0].id))?.description || ''}
                                                id={`${keyName}.description`}
                                                //rules={{ maxLength: 500 }}
                                                rows={3}
                                                placeholder={t('common.description')}
                                                disabled={isDetails}
                                            />
                                        </FormItem>

                                    </div>
                                </div>
                            })
                            }
                        </div>
                    </LanguagesTab>
                </div>


                <div className={styles.buttonContainer}>
                    <Button preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onCancel()} />

                    {isDetails && !role?.readOnly && hasRolesWritePolicy &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            onClick={() => showRemoveItemDialog({ id: role?.id } as RoleDto)} />
                    }
                   {isDetails &&  <Button type='button' text={t('common.edit')} onClick={() => goToEdit()} />}
                    {!isDetails && hasRolesWritePolicy &&
                        <Button
                            type='submit'
                            text={t('common.save')}
                            form="role-form" />
                    }
                </div>
            </form>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
        </div >
    );
};

export default RolesDetails;
